import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { usePendingSurgery } from '@hooks/register/surveys/use-pending-surgery';
import { useLocalize } from '@hooks/use-localize';
import { useSession } from '@hooks/security/use-session';
import { useStepForm } from '@hooks/use-step-form';
import { asStringBool } from '@utils/parse';
import { pendingSurgerySchema } from '@views/register/surveys/pending-surgery.schema';
import type { IPendingSurgeryFormValues, IPendingSurgeryModel, IPendingSurgerySubmitResult } from '@/types/surveys/pending-surgery';
import PageLoader from '@components/page-loader';
import FieldError from '@components/field-error';
import StarRating from '@views/register/surveys/star-rating';

const YES = '1';

const formatDate = (isoDate: string) => {
    const [year, month, day] = isoDate.split('-');

    return year && month && day ? `${month}/${day}/${year}` : '';
};

const PendingSurgery = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = usePendingSurgery(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <PendingSurgeryContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IPendingSurgeryContentProps {
    model: IPendingSurgeryModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IPendingSurgeryFormValues) => Promise<IPendingSurgerySubmitResult>;
}

const PendingSurgeryContent = ({ model, isSubmitting, submitError, onSubmit }: IPendingSurgeryContentProps) => {
    const t = useLocalize(model.languageData);
    const navigate = useNavigate();

    const {
        register,
        watch,
        setValue,
        onSubmit: handleFormSubmit,
        formState: { errors },
    } = useStepForm<IPendingSurgeryFormValues, IPendingSurgerySubmitResult>({
        resolver: zodResolver(pendingSurgerySchema),
        defaultValues: {
            hasBeenScheduled: asStringBool(model.hasBeenScheduled),
            scheduledDateTime: model.scheduledDateTime?.slice(0, 10) ?? '',
            hasCompleted: '',
            treatment: '',
            convenience: '',
            facility: '',
            commendations: '',
            comments: '',
        },
        submit: onSubmit,
        // The survey sits outside the screening / history step maps, so it routes itself.
        linkMap: [],
        onSuccess: () => navigate('/register/donate'),
    });

    const isScheduled = watch('hasBeenScheduled') === YES;
    const isCompleted = watch('hasCompleted') === YES;
    const scheduledDate = formatDate(watch('scheduledDateTime'));

    return (
        <div className='container'>
            <div className="row">
                <div className="col-xs-12">
                    <h1>{t('DashLabel_YourNextStepIsTheDonation', 'Next Step: The Donation')}</h1>
                    <p>
                        {t(
                            'DashLabel_YouCanKeepUsUpdatedAsYouReBeingScheduledOrJustComeDonation',
                            "You can keep us updated as you're being scheduled, or just come back here and complete the form after you've completed your donation. Once completed, we'll ask you to complete the Donor Satisfaction survey",
                        )}
                    </p>

                    <form className="form survey-form" onSubmit={handleFormSubmit}>
                        <div className="form-group">
                            <label className="control-label col-xs-12">
                                <FieldError message={errors.hasBeenScheduled?.message} />{' '}
                                {t('DashLabel_HaveYouBeenScheduledForDonation', 'Have you been scheduled for donation')}?
                            </label>
                            <div className="col-xs-12" style={{ marginLeft: 10 }}>
                                <label className="radio-inline">
                                    <input type="radio" value="1" {...register('hasBeenScheduled')} /> {t('Word_Yes', 'Yes')}
                                </label>
                                <label className="radio-inline">
                                    <input type="radio" value="0" {...register('hasBeenScheduled')} /> {t('Word_No', 'No')}
                                </label>
                            </div>
                        </div>

                        {isScheduled && (
                            <div className="sub-question">
                                <div className="form-group">
                                    <label className="control-label col-xs-12" htmlFor="ScheduledDateTime">
                                        <FieldError message={errors.scheduledDateTime?.message} />{' '}
                                        {t('DashLabel_WhatDateIsYourDonation', 'What date is your donation')}:
                                    </label>
                                    <div className="col-xs-12 col-sm-6">
                                        <input
                                            id="ScheduledDateTime"
                                            type="date"
                                            className="form-control"
                                            {...register('scheduledDateTime')}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="control-label col-xs-12">
                                        <FieldError message={errors.hasCompleted?.message} />{' '}
                                        {t('DashLabel_DidYouCompleteTheDonation', 'Did you complete the donation')}?
                                    </label>
                                    <div className="col-xs-12" style={{ marginLeft: 10 }}>
                                        <label className="radio-inline">
                                            <input type="radio" value="1" {...register('hasCompleted')} /> {t('Word_Yes', 'Yes')}
                                        </label>
                                        <label className="radio-inline">
                                            <input type="radio" value="0" {...register('hasCompleted')} /> {t('Word_No', 'No')}
                                        </label>
                                    </div>
                                </div>

                                {isCompleted && (
                                    <div className="sub-question">
                                        <p>
                                            {t(
                                                'DashLabel_OnAScaleOf1To51BeingTheLowestScoreAnd5BeingTheHighWorkUp',
                                                'On a scale of 1 to 5 (1 being the lowest score and 5 being the highest score) please rate the following aspects of your workup experience on',
                                            )}{' '}
                                            {scheduledDate}
                                        </p>

                                        <div className="form-group">
                                            <label className="control-label col-xs-12">
                                                <FieldError message={errors.treatment?.message} />{' '}
                                                {t('DashLabel_HowWellWereYouTreated', 'How well were you treated')}?
                                            </label>
                                            <div className="col-xs-12 col-sm-8">
                                                <StarRating
                                                    field={register('treatment')}
                                                    value={watch('treatment')}
                                                    onClear={() => setValue('treatment', '')}
                                                    t={t}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label className="control-label col-xs-12">
                                                <FieldError message={errors.convenience?.message} />{' '}
                                                {t('DashLabel_HowConvenientWasTheDonationProcess', 'How convenient was the donation process')}?
                                            </label>
                                            <div className="col-xs-12 col-sm-8">
                                                <StarRating
                                                    field={register('convenience')}
                                                    value={watch('convenience')}
                                                    onClear={() => setValue('convenience', '')}
                                                    t={t}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label className="control-label col-xs-12">
                                                <FieldError message={errors.facility?.message} />{' '}
                                                {t('DashLabel_HowWouldYouRateTheFacility', 'How would you rate the facility')}?
                                            </label>
                                            <div className="col-xs-12 col-sm-8">
                                                <StarRating
                                                    field={register('facility')}
                                                    value={watch('facility')}
                                                    onClear={() => setValue('facility', '')}
                                                    t={t}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label className="control-label col-xs-12" htmlFor="Commendations">
                                                <FieldError message={errors.commendations?.message} />{' '}
                                                {t(
                                                    'DashLabel_WasThereAnyoneWhoDidAnEspecially',
                                                    'Was there anyone who did an especially great job taking care of you that you would like to commend',
                                                )}
                                                ?
                                            </label>
                                            <div className="col-xs-12">
                                                <textarea
                                                    id="Commendations"
                                                    className="form-control"
                                                    rows={5}
                                                    maxLength={1000}
                                                    {...register('commendations')}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label className="control-label col-xs-12" htmlFor="Comments">
                                                <FieldError message={errors.comments?.message} />{' '}
                                                {t('DashLabel_HowCanWeImprove', 'How can we improve')}?
                                            </label>
                                            <div className="col-xs-12">
                                                <textarea
                                                    id="Comments"
                                                    className="form-control"
                                                    rows={5}
                                                    maxLength={1000}
                                                    {...register('comments')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {submitError && (
                            <div className="form-group">
                                <div className="col-xs-12">
                                    <div className="alert alert-danger">{submitError}</div>
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <div className="col-xs-12">
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                    {t('Word_Submit', 'Submit')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PendingSurgery;
